// HR policy chapters (step 4) — admin-editable, like career paths and Q&A.
// Body content is rich HTML (edited via the same RichTextEditor used for Q&A
// answers) so an admin can rewrite wording, add/remove chapters, or update
// figures without any code change. Defaults below preserve the original
// figures (sourced from official board resolutions) as clean HTML.

import { getAdminToken, getSetting, getPublic, putSetting } from "../lib/api";

export interface PolicyChapter {
  id: string;
  icon: string;
  titleAr: string;
  titleEn: string;
  introAr: string;
  introEn: string;
  bodyHtmlAr: string;
  bodyHtmlEn: string;
}

export const chapterTitle = (c: PolicyChapter, lang: "ar" | "en") => (lang === "ar" ? c.titleAr : c.titleEn);
export const chapterIntro = (c: PolicyChapter, lang: "ar" | "en") => (lang === "ar" ? c.introAr : c.introEn);
export const chapterBodyHtml = (c: PolicyChapter, lang: "ar" | "en") => (lang === "ar" ? c.bodyHtmlAr : c.bodyHtmlEn);

export const DEFAULT_POLICY_CHAPTERS: PolicyChapter[] = [
  {
    id: "attendance",
    icon: "⏱️",
    titleAr: "الحضور والانصراف والاستئذانات",
    titleEn: "Attendance, Departure & Permits",
    introAr: "يهدف تنظيم أوقات العمل إلى تحقيق بيئة عمل أكثر كفاءة ووضوحاً، ويبيّن هذا القسم مواعيد العمل، وآلية تسجيل الحضور والانصراف، وضوابط الاستئذانات والعمل عن بعد.",
    introEn: "Organizing working hours creates a more efficient, transparent workplace. This section covers working hours, attendance rules, and the controls for permits and remote work.",
    bodyHtmlAr:
      "<div class=\"kd-policy-split\"><div>" +
      "<ul>" +
      "<li><strong>أيام العمل:</strong> الأحد – الخميس</li>" +
      "<li><strong>وقت الحضور:</strong> بين 8:30 و9:30 صباحاً</li>" +
      "<li><strong>وقت الانصراف:</strong> بين الساعة 4:30 و5:30 مساءً</li>" +
      "<li><strong>ساعات العمل:</strong> 8 ساعات يومياً (دون احتساب ساعة الراحة)</li>" +
      "</ul>" +
      "<h3>الموظفون الذين يعملون بنظام المناوبات</h3>" +
      "<ul>" +
      "<li><strong>أيام العمل:</strong> 5 أيام بالأسبوع تُحدَّد كل شهر أو ربع سنوي.</li>" +
      "<li><strong>وقت الحضور والانصراف:</strong> تُحدَّد كل شهر أو ربع سنوي.</li>" +
      "<li><strong>ساعات العمل:</strong> تُحدَّد كل شهر أو ربع سنوي بما لا يتجاوز 40 ساعة بالأسبوع (دون احتساب ساعة الراحة)</li>" +
      "</ul>" +
      "</div><div>" +
      "<h3>الاستئذانات</h3><ul>" +
      "<li><strong>استئذان شخصي:</strong> 3 ساعات لليوم الواحد، بحد أقصى 12 ساعة شهرياً.</li>" +
      "<li><strong>استئذان مراجعة طبية:</strong> حتى 4 ساعات، ويشمل الموظف أو أحد أفراد أسرته.</li>" +
      "<li><strong>استئذان عمل جزئي:</strong> حسب حاجة العمل.</li>" +
      "<li><strong>استئذان عمل ليوم كامل:</strong> حسب طبيعة الوظيفة ومتطلبات العمل.</li>" +
      "</ul><p>(يُشترط توضيح سبب الاستئذان عند تقديم الطلب أو تقديم مرفق)</p>" +
      "<h3>العمل عن بعد</h3><p>12 يوماً في السنة، وبحد أقصى يومين في الشهر.</p><p>(لا تتداخل أيام العمل عن بعد مع الإجازات أو العطل الرسمية)</p>" +
      "</div></div>",
    bodyHtmlEn:
      "<div class=\"kd-policy-split\"><div>" +
      "<ul>" +
      "<li><strong>Working days:</strong> Sunday – Thursday</li>" +
      "<li><strong>Check-in:</strong> Between 8:30 and 9:30 AM</li>" +
      "<li><strong>Check-out:</strong> Between 4:30 and 5:30 PM</li>" +
      "<li><strong>Working hours:</strong> 8 hours a day (not counting the rest break)</li>" +
      "</ul>" +
      "<h3>Shift workers</h3>" +
      "<ul>" +
      "<li><strong>Working days:</strong> 5 days per week, set monthly or quarterly.</li>" +
      "<li><strong>Check-in/check-out:</strong> Set monthly or quarterly.</li>" +
      "<li><strong>Working hours:</strong> Set monthly or quarterly, capped at 40 hours per week (not counting the rest break)</li>" +
      "</ul>" +
      "</div><div>" +
      "<h3>Permits</h3><ul>" +
      "<li><strong>Personal permit:</strong> 3 hours per day, up to 12 hours per month.</li>" +
      "<li><strong>Medical appointment permit:</strong> Up to 4 hours, covers the employee or an immediate family member.</li>" +
      "<li><strong>Partial-day permit:</strong> As work needs require.</li>" +
      "<li><strong>Full-day permit:</strong> Depending on the role and work requirements.</li>" +
      "</ul><p>(The reason for the permit must be stated when submitting the request, or a supporting attachment provided)</p>" +
      "<h3>Remote work</h3><p>12 days per year, capped at 2 days per month.</p><p>(Remote-work days do not overlap with leaves or official holidays)</p>" +
      "</div></div>",
  },
  {
    id: "overtime",
    icon: "🕒",
    titleAr: "العمل الإضافي",
    titleEn: "Overtime",
    introAr: "تحرص كادن على تحقيق التوازن بين متطلبات العمل والحياة الشخصية، لذا يُنظَّم العمل الإضافي وفق الأنظمة والسياسات المعتمدة، بما يضمن حقوق الموظفين واستمرارية الأعمال بكفاءة.",
    introEn: "Kaden is committed to balancing work demands with personal life, so overtime is governed by approved policies that protect employee rights while keeping operations running efficiently.",
    bodyHtmlAr:
      "<h3>شروط العمل الإضافي</h3><ul>" +
      "<li>موافقة مسبقة من المدير المباشر.</li>" +
      "<li>لا تتجاوز 4 ساعات إضافية خلال أيام العمل.</li>" +
      "<li>لا تتجاوز 8 ساعات في أيام العطل.</li>" +
      "<li>تُحتسب ساعة العمل الإضافي بساعة ونصف.</li>" +
      "</ul>" +
      "<h3>داخل مقر الشركة</h3><p>يلزم إثبات ساعات العمل الإضافية بالبصمة.</p>" +
      "<h3>خارج مقر الشركة</h3><p>لا يلزم إثبات الحضور والانصراف إلا في حالات معينة، وإنما يلزم تقديم تقرير للمدير المباشر.</p>",
    bodyHtmlEn:
      "<h3>Overtime conditions</h3><ul>" +
      "<li>Prior approval from the direct manager.</li>" +
      "<li>No more than 4 extra hours on working days.</li>" +
      "<li>No more than 8 extra hours on holidays.</li>" +
      "<li>Overtime is calculated at 1.5× the regular hourly rate.</li>" +
      "</ul>" +
      "<h3>On company premises</h3><p>Overtime hours must be confirmed by fingerprint attendance.</p>" +
      "<h3>Off company premises</h3><p>Check-in/check-out is not required except in specific cases, but a report must be submitted to the direct manager.</p>",
  },
  {
    id: "leaves",
    icon: "🏖️",
    titleAr: "الإجازات",
    titleEn: "Leaves",
    introAr: "تتبنى الشركة سياسة إجازات شاملة ومرنة تدعم رفاهية الموظف وتوازنه بين العمل والحياة الشخصية.",
    introEn: "The company follows a comprehensive, flexible leave policy that supports employee wellbeing and work-life balance.",
    bodyHtmlAr:
      "<div class=\"kd-leave-grid\">" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🌿 الإجازة السنوية</span><div class=\"kd-leave-desc\">21 يوم عمل</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🤰 إجازة وضع</span><div class=\"kd-leave-desc\">وفق نظام العمل السعودي</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🕋 إجازة عمرة</span><div class=\"kd-leave-desc\">تُمنح إجازة العمرة مرة واحدة في السنة الميلادية</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🩺 الإجازة المرضية</span><div class=\"kd-leave-desc\">وفق نظام العمل السعودي</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">💍 إجازة الزواج</span><div class=\"kd-leave-desc\">5 أيام</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">👶 إجازة الأبوة</span><div class=\"kd-leave-desc\">3 أيام خلال 3 أشهر من تاريخ الولادة</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🕊️ إجازة الوفاة</span><div class=\"kd-leave-desc\"><ul><li>5 أيام من تاريخ الوفاة من الدرجة الأولى</li><li>3 أيام من تاريخ وفاة الأقارب من الدرجة الثانية</li></ul></div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🎓 إجازة اختبارات</span><div class=\"kd-leave-desc\">تُمنح للموظفين الحاصلين على موافقة لإكمال الدراسة، مع تقديم جدول الاختبارات، وتُمنح للاختبارات المقدَّمة لأول مرة فقط ولا تشمل إعادة الاختبار.</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">👥 إجازة اضطرارية<span class=\"kd-leave-pill-sub\">10 أيام خلال السنة الميلادية</span></span><div class=\"kd-leave-desc\"><ul><li>يوم واحد إجازة زواج قريب من الدرجة الأولى أو الثانية</li><li>إجازة مرافق من الدرجة الأولى، 5 أيام متتالية بحد أقصى</li><li>يوم واحد إجازة وفاة قريب من الدرجة الثالثة</li></ul></div></div>" +
      "</div>",
    bodyHtmlEn:
      "<div class=\"kd-leave-grid\">" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🌿 Annual leave</span><div class=\"kd-leave-desc\">21 working days</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🤰 Maternity leave</span><div class=\"kd-leave-desc\">Per Saudi labor law</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🕋 Umrah leave</span><div class=\"kd-leave-desc\">Granted once per calendar year</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🩺 Sick leave</span><div class=\"kd-leave-desc\">Per Saudi labor law</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">💍 Marriage leave</span><div class=\"kd-leave-desc\">5 days</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">👶 Paternity leave</span><div class=\"kd-leave-desc\">3 days within 3 months of the birth</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🕊️ Bereavement leave</span><div class=\"kd-leave-desc\"><ul><li>5 days for a first-degree relative</li><li>3 days for a second-degree relative</li></ul></div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">🎓 Exam leave</span><div class=\"kd-leave-desc\">For employees approved to complete their studies, on submission of the exam schedule. Granted for first-attempt exams only, not retakes.</div></div>" +
      "<div class=\"kd-leave-card\"><span class=\"kd-leave-pill\">👥 Emergency leave<span class=\"kd-leave-pill-sub\">10 days per calendar year</span></span><div class=\"kd-leave-desc\"><ul><li>1 day for a first- or second-degree relative's marriage</li><li>Companion leave for a first-degree relative, up to 5 consecutive days</li><li>1 day for a third-degree relative's passing</li></ul></div></div>" +
      "</div>",
  },
  {
    id: "travel",
    icon: "✈️",
    titleAr: "الانتداب",
    titleEn: "Business Travel",
    introAr: "عند تكليف الموظف بمهمة عمل، تتحمّل الشركة تكاليف الانتداب والسفر وفق السياسات المعتمدة، بما يضمن عدم تحمّل الموظف أي مصروفات شخصية مرتبطة بمهمة العمل.",
    introEn: "When an employee is assigned a business trip, the company covers travel and per-diem costs per approved policy, so the employee bears no personal expense tied to the assignment.",
    bodyHtmlAr:
      "<table class=\"kd-policy-table-auto\"><thead><tr><th>الدرجة الوظيفية</th><th>تذاكر السفر</th><th>نسب توزيع الانتداب (سكن، نقل، إعاشة)</th><th>بدل الانتداب</th></tr></thead><tbody>" +
      "<tr><td><strong>6 – 15</strong></td><td>الدرجة الاقتصادية</td><td>50% للسكن / 25% للنقل / 25% للإعاشة</td><td>800 ريال داخل المملكة، 1,200 ريال خارج المملكة</td></tr>" +
      "<tr><td><strong>16 – 20</strong></td><td>درجة رجال الأعمال</td><td>50% للسكن / 25% للنقل / 25% للإعاشة</td><td>950 ريال داخل المملكة، 1,400 ريال خارج المملكة</td></tr>" +
      "</tbody></table><p class=\"kd-policy-center\"><strong>ملاحظة:</strong> في حال توفير السكن والنقل والإعاشة بالكامل، يُصرف للموظف 25% من قيمة بدل الانتداب.</p>",
    bodyHtmlEn:
      "<table class=\"kd-policy-table-auto\"><thead><tr><th>Grade</th><th>Tickets</th><th>Per-diem split (Housing, Transport, Living)</th><th>Travel allowance</th></tr></thead><tbody>" +
      "<tr><td><strong>6 – 15</strong></td><td>Economy class</td><td>Housing 50% / Transport 25% / Living 25%</td><td>SAR 800 inside KSA, SAR 1,200 abroad</td></tr>" +
      "<tr><td><strong>16 – 20</strong></td><td>Business class</td><td>Housing 50% / Transport 25% / Living 25%</td><td>SAR 950 inside KSA, SAR 1,400 abroad</td></tr>" +
      "</tbody></table><p class=\"kd-policy-center\"><strong>Note:</strong> If the company fully provides housing, transport and meals, only 25% of the travel allowance is paid.</p>",
  },
  {
    id: "performance-goals",
    icon: "🎯",
    titleAr: "إدارة الأداء والأهداف السنوية",
    titleEn: "Performance & Annual Goals Management",
    introAr: "تعتمد كادن نظاماً لإدارة الأداء يهدف إلى مواءمة أهداف الموظفين مع أهداف الشركة، وتعزيز التطور المستمر، وتقييم الأداء بعدالة وشفافية، بما يسهم في تحقيق التميز المؤسسي.",
    introEn: "Kaden runs a performance management system that aligns employee goals with company goals, encourages continuous development, and evaluates performance fairly and transparently.",
    bodyHtmlAr:
      "<h3>مراحل إدارة الأداء الوظيفي</h3>" +
      "<h3>المرحلة الأولى، إعداد الأهداف السنوية</h3><p>تبدأ بعد اعتماد أهداف الشركة وأهداف الإدارات، حيث يتفق الموظف ومديره المباشر على الأهداف السنوية وفق منهجية SMART، مع تحديد:</p><ul>" +
      "<li>الأهداف الوظيفية.</li>" +
      "<li>مؤشرات الأداء (KPIs).</li>" +
      "<li>وحدة القياس.</li>" +
      "<li>درجة أهمية كل هدف.</li>" +
      "</ul>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">نموذج الأهداف</div><img src=\"/policies/goals-template.png\" alt=\"نموذج الأهداف\" /></figure>" +
      "<h3>المرحلة الثانية، تنفيذ ومتابعة الأداء</h3><p>يعمل الموظف على تحقيق أهدافه خلال العام، مع متابعة دورية من المدير المباشر عند الحاجة، وتقديم الدعم اللازم لمعالجة أي تحديات قد تؤثر على تحقيق الأهداف.</p>" +
      "<h3>المرحلة الثالثة، التقييم السنوي</h3><p>في نهاية العام يُقيَّم الموظف من خلال:</p><ul>" +
      "<li><strong>تقييم تحقيق الأهداف:</strong> وفق النتائج الفعلية ومؤشرات الأداء المعتمدة، وتمثل 70% من إجمالي التقييم.</li>" +
      "<li><strong>تقييم الجدارات الوظيفية:</strong> وفق السلوكيات والكفاءات المطلوبة للوظيفة، وتمثل 30% من إجمالي التقييم.</li>" +
      "</ul>" +
      "<div class=\"kd-policy-split\">" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">تقييم الجدارات الوظيفية</div><img src=\"/policies/perf-competency.png\" alt=\"تقييم الجدارات الوظيفية\" /></figure>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">تقييم تحقيق الأهداف</div><img src=\"/policies/perf-goal-achievement.png\" alt=\"تقييم تحقيق الأهداف\" /></figure>" +
      "</div>",
    bodyHtmlEn:
      "<h3>Performance management stages</h3>" +
      "<h3>Stage one, setting annual goals</h3><p>Begins once company and department goals are approved. The employee and their direct manager agree on annual goals following the SMART methodology, defining:</p><ul>" +
      "<li>Role objectives.</li>" +
      "<li>Performance indicators (KPIs).</li>" +
      "<li>The unit of measurement.</li>" +
      "<li>The weight of each goal.</li>" +
      "</ul>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">Goals template</div><img src=\"/policies/goals-template.png\" alt=\"Goals template\" /></figure>" +
      "<h3>Stage two, execution and follow-up</h3><p>The employee works toward their goals throughout the year, with periodic follow-up from the direct manager as needed, and support to address any challenges affecting goal achievement.</p>" +
      "<h3>Stage three, annual evaluation</h3><p>At year-end, the employee is evaluated on:</p><ul>" +
      "<li><strong>Goal achievement:</strong> Against actual results and approved KPIs, representing 70% of the overall score.</li>" +
      "<li><strong>Role competencies:</strong> Against the behaviors and skills required for the job, representing 30% of the overall score.</li>" +
      "</ul>" +
      "<div class=\"kd-policy-split\">" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">Competency evaluation</div><img src=\"/policies/perf-competency.png\" alt=\"Competency evaluation template\" /></figure>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">Goal achievement evaluation</div><img src=\"/policies/perf-goal-achievement.png\" alt=\"Goal achievement evaluation template\" /></figure>" +
      "</div>",
  },
  {
    id: "rewards",
    icon: "💰",
    titleAr: "العلاوة والمكافآت السنوية",
    titleEn: "Annual Raise & Bonus",
    introAr: "تُقدّر كادن جهود موظفيها من خلال نظام للعلاوات والمكافآت السنوية يرتبط بأداء الموظف وأداء الشركة، بما يعزز التميز والالتزام. تبدأ دورة التقييم السنوي في نهاية كل عام عن أداء العام المنقضي.",
    introEn: "Kaden recognizes its employees' efforts through an annual raise and bonus system tied to both employee and company performance, reinforcing excellence and commitment. The annual evaluation cycle begins at the end of each year, covering the year just ended.",
    // Rebuilt (again) per the user's explicit direction: use the actual
    // table images exported from the 2026 employee handbook (دليل الموظف2)
    // instead of hand-built HTML tables, matching the slide's own two
    // side-by-side images exactly. The older board-resolution rowspan
    // matrix and its eligibility-rules list are dropped entirely — this
    // handbook slide is now the explicit, sole source of truth for this
    // chapter's content and layout.
    bodyHtmlAr:
      "<div class=\"kd-policy-split\"><div>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">العلاوة السنوية</div><img src=\"/policies/reward-raise-table.png\" alt=\"العلاوة السنوية\" /></figure>" +
      "<p class=\"kd-policy-center\"><strong>أهلية الاستحقاق:</strong> إكمال سنة بفترة التقييم لتنطبق سياسة العلاوة السنوية.</p>" +
      "</div><div>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">المكافأة السنوية</div><img src=\"/policies/reward-bonus-table.png\" alt=\"المكافأة السنوية\" /></figure>" +
      "<p class=\"kd-policy-center\"><strong>أهلية الاستحقاق:</strong> إكمال 6 أشهر بفترة التقييم لتنطبق سياسة المكافأة السنوية.</p>" +
      "</div></div>" +
      "<p class=\"kd-policy-center\"><strong>ملاحظة:</strong> يتم صرف العلاوات والمكافآت السنوية وفقاً للمركز المالي للشركة، وبعد اعتماد الموازنة السنوية، وما يصدر من موافقات وقرارات من مجلس الإدارة واللجنة التنفيذية.</p>",
    bodyHtmlEn:
      "<div class=\"kd-policy-split\"><div>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">Annual raise</div><img src=\"/policies/reward-raise-table.png\" alt=\"Annual raise table\" /></figure>" +
      "<p class=\"kd-policy-center\"><strong>Eligibility:</strong> One full year within the evaluation period for the annual raise policy to apply.</p>" +
      "</div><div>" +
      "<figure class=\"kd-perf-figure\"><div class=\"kd-policy-figure-label\">Annual bonus</div><img src=\"/policies/reward-bonus-table.png\" alt=\"Annual bonus table\" /></figure>" +
      "<p class=\"kd-policy-center\"><strong>Eligibility:</strong> 6 months within the evaluation period for the annual bonus policy to apply.</p>" +
      "</div></div>" +
      "<p class=\"kd-policy-center\"><strong>Note:</strong> Annual raises and bonuses are paid based on the company's financial position, after the annual budget is approved, and any approvals or resolutions issued by the Board of Directors and the Executive Committee.</p>",
  },
  {
    id: "benefits",
    icon: "🎁",
    titleAr: "المزايا والبرامج الوظيفية",
    titleEn: "Benefits & Career Programs",
    introAr: "تحرص كادن على توفير بيئة عمل جاذبة من خلال مجموعة من المزايا والبرامج التي تدعم الموظفين مهنياً وشخصياً، وتُسهم في تعزيز الاستقرار الوظيفي وتحفيز الأداء المتميز.",
    introEn: "Kaden provides an attractive workplace through a set of benefits and programs that support employees both professionally and personally, reinforcing job stability and rewarding outstanding performance.",
    bodyHtmlAr:
      "<div class=\"kd-policy-split\"><div>" +
      "<h3>الترقية والزيادة</h3><p>يُشترط للنظر في الترقية أو الزيادة:</p><ul>" +
      "<li>مضي 3 سنوات على آخر ترقية أو زيادة (باستثناء العلاوة السنوية).</li>" +
      "<li>الحصول على تقييم أداء (يحقق التوقعات - ممتاز) أو أعلى خلال آخر 3 سنوات.</li>" +
      "<li>خلو السجل الوظيفي من المخالفات التأديبية.</li>" +
      "</ul>" +
      "<h3>تأمين طبي</h3><p>يشمل الموظف وأفراد عائلته.</p>" +
      "<h3>إجازات إضافية</h3><p>إجازة عمرة والإجازات الاضطرارية.</p>" +
      "</div><div>" +
      "<h3>نقل ملكية جهاز العمل</h3><p>تنتقل ملكية الكمبيوتر المحمول إلى الموظف بعد مرور 4 سنوات من الاستخدام، ويُعطى الموظف جهازاً جديداً للعمل عليه.</p>" +
      "<h3>دعم تعليم الأبناء</h3><p>دعم تعليم أبناء الموظفين للدرجة الوظيفية 16 فأعلى، وفق السياسة المعتمدة.</p>" +
      "<h3>التدريب والتأهيل للشهادات المهنية</h3><p>دعم الحصول على الشهادات المهنية والدورات التدريبية وفق الاحتياج الوظيفي وميزانية التدريب المعتمدة.</p>" +
      "</div></div>",
    bodyHtmlEn:
      "<div class=\"kd-policy-split\"><div>" +
      "<h3>Promotion and raise</h3><p>To be considered for promotion or a raise:</p><ul>" +
      "<li>3 years must have passed since the last promotion or raise (the annual raise excluded).</li>" +
      "<li>A performance rating of \"Met expectations\" or higher (up to \"Outstanding\") in the last 3 years.</li>" +
      "<li>A clean disciplinary record.</li>" +
      "</ul>" +
      "<h3>Medical insurance</h3><p>Covers the employee and their family.</p>" +
      "<h3>Additional leaves</h3><p>Umrah leave and emergency leaves.</p>" +
      "</div><div>" +
      "<h3>Work device ownership transfer</h3><p>Ownership of the laptop transfers to the employee after 4 years of use, and a new device is issued for work.</p>" +
      "<h3>Children's education support</h3><p>Support for employees' children's education for Grade 16 and above, per approved policy.</p>" +
      "<h3>Training and professional certification</h3><p>Support for professional certifications and training courses based on role needs and the approved training budget.</p>" +
      "</div></div>",
  },
  {
    id: "culture",
    icon: "☕",
    titleAr: "الثقافة والبرامج الداخلية",
    titleEn: "Culture & Internal Programs",
    introAr: "تحرص كادن على بناء بيئة عمل إيجابية من خلال برامج ومبادرات تعزز التواصل بين الموظفين، وتدعم التطوير المهني، وترسّخ ثقافة التقدير والانتماء والابتكار، بما يسهم في رفع تجربة الموظف وتحقيق مستهدفات الشركة.",
    introEn: "Kaden builds a positive workplace through programs and initiatives that strengthen communication among employees, support professional development, and reinforce a culture of recognition, belonging, and innovation.",
    bodyHtmlAr:
      "<h3>☕ كادن كافيه</h3><p>لقاء شهري يجمع الرئيس التنفيذي بمجموعة من الموظفين من مختلف الإدارات، بهدف تبادل الآراء والمقترحات، وتعزيز التواصل والانفتاح بين الإدارة والموظفين.</p>" +
      "<h3>🎉 التفاعل الاجتماعي والمناسبات الخاصة للموظفين</h3><p>تشارك الشركة موظفيها مناسباتهم الاجتماعية، مثل الزواج، والمولود الجديد، والمنزل الجديد، والعمليات الجراحية، وغيرها، بما يعزز الترابط والتقدير المتبادل.</p>" +
      "<h3>🎊 فعاليات داخلية</h3><p>تنظيم فعاليات ومناسبات داخلية على مدار العام، مثل الحفل السنوي، ويوم الأم، ويوم الأب، ويوم الطفل، وغيرها من المبادرات التي تعزز بيئة العمل.</p>",
    bodyHtmlEn:
      "<h3>☕ Kaden Cafe</h3><p>A monthly gathering bringing the CEO together with employees from different departments to exchange ideas and suggestions, and to strengthen open communication between management and staff.</p>" +
      "<h3>🎉 Social engagement and personal milestones</h3><p>The company shares in employees' personal milestones, such as marriage, a new child, a new home, surgeries, and more, reinforcing connection and mutual appreciation.</p>" +
      "<h3>🎊 Internal events</h3><p>Internal events and occasions are organized throughout the year, such as the annual celebration, Mother's Day, Father's Day, Children's Day, and other initiatives that strengthen the workplace.</p>",
  },
  {
    id: "initiatives",
    icon: "🏆",
    titleAr: "البرامج والمبادرات",
    titleEn: "Programs & Initiatives",
    introAr: "تحرص كادن على تنفيذ برامج ومبادرات متنوعة تهدف إلى تعزيز بيئة العمل، ودعم تطوير الموظفين، وترسيخ ثقافة التميز والابتكار، بما يسهم في تحقيق مستهدفات الشركة ورفع تجربة الموظف.",
    introEn: "Kaden runs a variety of programs and initiatives aimed at strengthening the workplace, supporting employee development, and reinforcing a culture of excellence and innovation.",
    bodyHtmlAr:
      "<h3>🤝 برنامج المحافظة على الموظفين</h3><p>يستهدف البرنامج المحافظة على الموظفين المتميزين من خلال تقديم مزايا إضافية، وتعزيز فرصهم في التطور الوظيفي والترشّح للمناصب القيادية، بما يعزز الاستقرار الوظيفي واستدامة الكفاءات داخل الشركة.</p>" +
      "<h3>🏆 مكافأة العمل المتميّز</h3><p>تُمنح بشكل نصف سنوي لثلاثة موظفين من أصحاب الأداء الاستثنائي في مختلف الإدارات، تقديراً لجهودهم وإسهامهم في تطوير بيئة العمل.</p><ul>" +
      "<li><strong>المركز الأول:</strong> 40,000 ريال.</li>" +
      "<li><strong>المركز الثاني:</strong> 20,000 ريال.</li>" +
      "<li><strong>المركز الثالث:</strong> 10,000 ريال.</li>" +
      "</ul>" +
      "<h3>👨‍👩‍👧 برنامج إعالة أسرة الموظف المتوفى</h3><ul>" +
      "<li><strong>فئة (أ):</strong> إذا تجاوزت الخدمة 10 سنوات، يُصرف 50% من الراتب الأساسي.</li>" +
      "<li><strong>فئة (ب):</strong> إذا تجاوزت الخدمة 5 سنوات، يُصرف 25% من الراتب الأساسي.</li>" +
      "<li>مدة الصرف تساوي 50% من مدة الخدمة التي قضاها الموظف.</li>" +
      "</ul>",
    bodyHtmlEn:
      "<h3>🤝 Employee Retention Program</h3><p>Targets outstanding employees with additional benefits and better opportunities for career growth and leadership roles, reinforcing job stability and the sustainability of talent within the company.</p>" +
      "<h3>🏆 Excellence Award</h3><p>Awarded twice a year to three employees with exceptional performance across departments, in recognition of their efforts and contribution to the workplace.</p><ul>" +
      "<li><strong>1st place:</strong> SAR 40,000.</li>" +
      "<li><strong>2nd place:</strong> SAR 20,000.</li>" +
      "<li><strong>3rd place:</strong> SAR 10,000.</li>" +
      "</ul>" +
      "<h3>👨‍👩‍👧 Deceased Employee's Family Support Program</h3><ul>" +
      "<li><strong>Category (A):</strong> For over 10 years of service, 50% of base salary is paid.</li>" +
      "<li><strong>Category (B):</strong> For over 5 years of service, 25% of base salary is paid.</li>" +
      "<li>The payment period equals 50% of the employee's length of service.</li>" +
      "</ul>",
  },
  {
    id: "development",
    icon: "🎓",
    titleAr: "التطوير المهني، برنامج نخبة كادن",
    titleEn: "Professional Development, Kaden Elite Program",
    introAr: "طوّرت كادن برنامج نخبة (2026–2027) لتطوير القيادات والكفاءات المهنية، وفق أحدث الممارسات العالمية، وربطت مساراته بالجدارات الوظيفية لكل مستوى، بما يضمن مواءمة رحلة التطوير مع احتياجات الموظف ومستهدفات الشركة، ويسهم في إعداد كوادر عالية الأداء وجاهزة للمستقبل.",
    introEn: "Kaden developed the Elite Program (2026–2027) to develop leaders and professional talent, following current global best practices, with tracks tied to the competencies of each level, ensuring the development journey aligns with both employee needs and company goals.",
    bodyHtmlAr:
      "<figure class=\"kd-perf-figure\"><img src=\"/policies/elite-program.png\" alt=\"برنامج نخبة كادن\" /></figure>" +
      "<p>يتضمن البرنامج:</p><ul>" +
      "<li>ستة دورات تدريبية موزّعة على مدار البرنامج.</li>" +
      "<li>15 مقعد شهادات مهنية على مدار البرنامج.</li>" +
      "<li>ثلاثة برامج فنية وتخصصية.</li>" +
      "<li>منصات تعلم إلكترونية وبرامج تطوير ذاتي داعمة لرحلة التعلم.</li>" +
      "<li>ثلاثة برامج في المهارات السلوكية والقيادية.</li>" +
      "</ul>",
    bodyHtmlEn:
      "<figure class=\"kd-perf-figure\"><img src=\"/policies/elite-program.png\" alt=\"Kaden Elite Program\" /></figure>" +
      "<p>The program includes:</p><ul>" +
      "<li>Six training courses spread across the program.</li>" +
      "<li>15 professional-certification seats across the program.</li>" +
      "<li>Three technical and specialized programs.</li>" +
      "<li>E-learning platforms and self-development programs supporting the learning journey.</li>" +
      "<li>Three programs in behavioral and leadership skills.</li>" +
      "</ul>",
  },
  {
    id: "grievance",
    icon: "⚖️",
    titleAr: "التظلم",
    titleEn: "Grievances",
    introAr: "تحرص كادن على توفير آليات عادلة وشفافة للنظر في تظلم الموظفين وضمان حقوقهم كاملةً.",
    introEn: "Kaden provides fair and transparent mechanisms for reviewing employee grievances and ensuring their full rights.",
    bodyHtmlAr:
      "<h3>أنواع التظلم</h3><ul>" +
      "<li><strong>التظلم على قرار إداري:</strong> يُقدَّم خلال 3 أيام عمل من تاريخ إشعار الموظف بصدور القرار أو الإجراء المتظلَّم منه.</li>" +
      "<li><strong>التظلم المرتبط بتقييم الأداء السنوي:</strong> يُقدَّم خلال 5 أيام عمل من تاريخ استلام تقييم الأداء (وفقاً للسياسة الداخلية صفحة رقم 111).</li>" +
      "</ul>" +
      "<h3>خطوات التظلم</h3><ol>" +
      "<li><strong>تقديم التظلم:</strong> يتقدّم الموظف بطلب تظلم مكتوب إلى الموارد البشرية، موضّحاً القرار أو تقييم الأداء محل الاعتراض، وأسباب التظلم، مع إرفاق أي مستندات داعمة.</li>" +
      "<li><strong>استلام التظلم وتسجيله:</strong> تستلم الموارد البشرية التظلم وتسجّله وتراجعه وتُقيّمه من الناحية الشكلية والموضوعية، وتُبلغ الموظف بقبوله من عدمه.</li>" +
      "<li><strong>مراجعة التظلم:</strong> يتم دراسة التظلم بمراجعة المستندات ذات العلاقة، وسماع وجهات نظر الأطراف المعنية عند الحاجة.</li>" +
      "<li><strong>اتخاذ القرار:</strong> تُصدر اللجنة المختصة قرارها بعد استكمال المراجعة، ويكون القرار إما بتأييد القرار السابق أو تعديله أو إلغائه وفقاً لما تسفر عنه الدراسة.</li>" +
      "<li><strong>إبلاغ الموظف بالنتيجة:</strong> يتم إشعار الموظف بالقرار النهائي على الطلب المرفوع والإجراءات المرتبطة به.</li>" +
      "</ol>",
    bodyHtmlEn:
      "<h3>Types of grievances</h3><ul>" +
      "<li><strong>Grievance against an administrative decision:</strong> Submitted within 3 working days from the date the employee is notified of the decision or action being contested.</li>" +
      "<li><strong>Grievance related to the annual performance evaluation:</strong> Submitted within 5 working days from the date the performance evaluation is received (per internal policy, page 111).</li>" +
      "</ul>" +
      "<h3>Grievance steps</h3><ol>" +
      "<li><strong>Filing the grievance:</strong> The employee submits a written grievance request to HR, specifying the decision or evaluation being contested, the reasons, with any supporting documents attached.</li>" +
      "<li><strong>Receiving and logging:</strong> HR receives and logs the grievance, reviews it for form and substance, and informs the employee whether it is accepted.</li>" +
      "<li><strong>Review:</strong> The grievance is studied by reviewing relevant documents and hearing the concerned parties' views as needed.</li>" +
      "<li><strong>Decision:</strong> The relevant committee issues its decision once the review is complete, either upholding, amending, or overturning the original decision based on the findings.</li>" +
      "<li><strong>Notifying the employee:</strong> The employee is notified of the final decision on their submitted request and the related actions.</li>" +
      "</ol>",
  },
  {
    id: "appearance",
    icon: "👔",
    titleAr: "المظهر المهني",
    titleEn: "Professional Appearance",
    introAr: "يعكس المظهر المهني هوية كادن واحترافيتها، ويُسهم في تكوين انطباع إيجابي لدى العملاء والزملاء، لذا يلتزم جميع الموظفين بالزي الرسمي وبطاقة التعريف والمحافظة على بيئة عمل منظمة.",
    introEn: "A professional appearance reflects Kaden's identity and professionalism, and helps create a positive impression with clients and colleagues, so all employees are expected to follow the dress code, wear their ID badge, and maintain a tidy workplace.",
    bodyHtmlAr:
      "<h3>الزي الرسمي</h3><p>يلتزم الموظفون بالزي الرسمي المعتمد بما يعكس هوية الشركة واحترافيتها، وفق الضوابط المعتمدة.</p>" +
      "<h3>بطاقة الموظف</h3><p>يلتزم جميع الموظفين بارتداء بطاقة التعريف الرسمية في مكان ظاهر طوال ساعات العمل داخل مقرات الشركة.</p>" +
      "<h3>المحافظة على بيئة العمل</h3><p>يلتزم جميع الموظفين بالمحافظة على نظافة المكاتب والمرافق العامة، وعدم إجراء أي تعديلات أو إضافات على المكاتب أو الأثاث دون موافقة، بما يسهم في الحفاظ على بيئة عمل منظمة وآمنة.</p>",
    bodyHtmlEn:
      "<h3>Official dress code</h3><p>Employees follow the approved official dress code, reflecting the company's identity and professionalism, per the approved guidelines.</p>" +
      "<h3>Employee badge</h3><p>All employees must wear their official ID badge visibly at all times during working hours on company premises.</p>" +
      "<h3>Keeping the workplace tidy</h3><p>All employees must keep offices and shared facilities clean, and must not modify or add to desks or furniture without approval, helping maintain an organized, safe workplace.</p>",
  },
  {
    id: "hr-contact",
    icon: "📞",
    titleAr: "تواصل مع فريق الموارد البشرية",
    titleEn: "Contact the HR Team",
    introAr: "نرحب بأسئلتكم ومشاركتكم الفعّالة في تعزيز ثقافة كادن، نسعد بخدمتكم من الأحد إلى الخميس من الساعة 08:30 صباحاً وحتى 05:30 مساءً.",
    introEn: "We welcome your questions and your active participation in strengthening Kaden's culture. We're happy to help, Sunday to Thursday, 8:30 AM to 5:30 PM.",
    bodyHtmlAr:
      "<p><strong>البريد الإلكتروني:</strong> hr@kaden-sa.com</p>" +
      "<p><strong>الموقع:</strong> المقر الرئيسي، الدور الثاني، إدارة الموارد البشرية.</p>" +
      "<h3>فريق الموارد البشرية</h3><ul>" +
      "<li><strong>تركي الصالح</strong>، رئيس قسم الموارد البشرية، تحويلة 307</li>" +
      "<li><strong>منار راعي</strong>، أخصائي أول موارد بشرية، تحويلة 306</li>" +
      "<li><strong>هنا الخزيم</strong>، أخصائي أول موارد بشرية، تحويلة 343</li>" +
      "<li><strong>أفنان العقيلي</strong>، مشرف موارد بشرية، تحويلة 317</li>" +
      "<li><strong>فيصل النوفل</strong>، أخصائي موارد بشرية، تحويلة 330</li>" +
      "<li><strong>سليمان المشاري</strong>، مدير إدارة خدمات الشركة الأساسية، تحويلة 335</li>" +
      "</ul>",
    bodyHtmlEn:
      "<p><strong>Email:</strong> hr@kaden-sa.com</p>" +
      "<p><strong>Location:</strong> Head office, 2nd floor, HR Department.</p>" +
      "<h3>HR team</h3><ul>" +
      "<li><strong>Turki Al-Saleh</strong>, Head of HR, ext. 307</li>" +
      "<li><strong>Manar Rai</strong>, Senior HR Specialist, ext. 306</li>" +
      "<li><strong>Hana Al-Khuzaim</strong>, Senior HR Specialist, ext. 343</li>" +
      "<li><strong>Afnan Al-Aqeeli</strong>, HR Supervisor, ext. 317</li>" +
      "<li><strong>Faisal Al-Nawfal</strong>, HR Specialist, ext. 330</li>" +
      "<li><strong>Sulaiman Al-Mashari</strong>, Corporate Services Manager, ext. 335</li>" +
      "</ul>",
  },
];

// --- Runtime store (admin can override via the "policies" setting) ---
const POLICIES_CHANGED = "kd-policies-changed";
let current: PolicyChapter[] = [...DEFAULT_POLICY_CHAPTERS];

export function getPolicyChapters(): PolicyChapter[] {
  return current;
}

export function subscribePolicyChapters(cb: () => void): () => void {
  window.addEventListener(POLICIES_CHANGED, cb);
  return () => window.removeEventListener(POLICIES_CHANGED, cb);
}

function emit() {
  try {
    window.dispatchEvent(new Event(POLICIES_CHANGED));
  } catch {
    /* ignore */
  }
}

/** Load any admin-configured chapter overrides (public read, or admin read when signed in). */
export async function loadPolicyChapters(): Promise<void> {
  const res = getAdminToken() ? await getSetting<PolicyChapter[]>("policies") : await getPublic<{ value?: PolicyChapter[] } | PolicyChapter[]>("policies");
  const value = Array.isArray((res.data as any)?.value) ? (res.data as any).value : res.data;
  if (Array.isArray(value) && value.length > 0) current = value as PolicyChapter[];
  emit();
}

// --- Admin editing ---
export function newPolicyChapter(): PolicyChapter {
  return { id: "", icon: "📄", titleAr: "", titleEn: "", introAr: "", introEn: "", bodyHtmlAr: "", bodyHtmlEn: "" };
}
export function isBuiltInChapter(id: string): boolean {
  return DEFAULT_POLICY_CHAPTERS.some((c) => c.id === id);
}
export async function savePolicyChapters(chapters: PolicyChapter[]): Promise<{ ok: boolean; error?: string }> {
  current = chapters;
  emit();
  const res = await putSetting("policies", chapters);
  return res.ok ? { ok: true } : { ok: false, error: res.error };
}
export function resetPolicyChapters(): Promise<{ ok: boolean; error?: string }> {
  return savePolicyChapters([...DEFAULT_POLICY_CHAPTERS]);
}
